/* ============================================================================
   sbd-polish.js — behaviour for the presentation fixes in sbd-polish.css
   ============================================================================
   ITEM 2   shiny dot tracing the first letter of every H1
   ITEM 5   chat launcher no longer covers the buy button on phones
   ITEM 10  chat launcher becomes a small round icon button on phones

   Items 5 and 10 have to be done from JavaScript: the widget renders inside
   #vivi-widget-host's shadow root, and a stylesheet in the document cannot
   cross that boundary. The root is open, so a <style> is appended into it.
   ========================================================================== */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var MOBILE = '(max-width: 749px)';

  /* ══════════════════════════════════════════════════════════════════════
     ITEM 2 · Shiny dot along the strokes of the first letter
     ══════════════════════════════════════════════════════════════════════

     The dot follows the true outline of the glyph. The letter is re-rendered as
     an SVG <text> and stroked with a very short dash, which the browser lays
     along the actual glyph contours in the actual font — so no per-letter path
     data is needed and it works for any character in any typeface.

     A letter with a counter ("O", "R", "e") has more than one contour. SVG
     restarts the dash pattern on each subpath, so those letters get a dot on
     each contour travelling in parallel — which reads as intended rather than
     as a bug, so it is left alone.
  */

  var SHINE_DASH = 10;      // length of the bright segment, px
  var SHINE_SELECTOR = 'h1';

  function fontShorthand(cs) {
    // Canvas needs a single font shorthand; build it from the computed style.
    return [
      cs.fontStyle || 'normal',
      cs.fontVariant || 'normal',
      cs.fontWeight || '400',
      cs.fontSize + '/' + (cs.lineHeight === 'normal' ? 'normal' : cs.lineHeight),
      cs.fontFamily
    ].join(' ');
  }

  var measureCtx = null;

  function firstLetterIndex(text) {
    // Skip leading whitespace and punctuation — the first *letter*, not the
    // first character, so a heading opening with a quote still works.
    for (var i = 0; i < text.length; i++) {
      if (/[\p{L}\p{N}]/u.test(text[i])) return i;
    }
    return -1;
  }

  function firstTextNode(root) {
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: function (n) {
        return /\S/.test(n.nodeValue) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
      }
    });
    return walker.nextNode();
  }

  /* The letter is rendered ONCE, as a single SVG <text> that carries both the
     fill (the letter you read) and the animated stroke (the dot that travels
     its contours).

     An earlier version layered a stroke-only SVG on top of the original HTML
     letter. That put the character in the DOM twice, so the H1 read as
     "YYour vision" to anything extracting text — including search engines — and
     it had to keep two independently-laid-out glyphs in alignment. One element
     removes both problems: alignment is now structural rather than computed. */

  function buildShine(h1) {
    if (h1.dataset.sbdShine) return;

    var node = firstTextNode(h1);
    if (!node) return;
    var idx = firstLetterIndex(node.nodeValue);
    if (idx < 0) return;

    var letter = node.nodeValue[idx];

    /* The SVG is an atomic inline, which introduces a line-break opportunity
       between it and the character after it. On the hero H1 that was enough for
       `text-wrap: balance` to break the heading immediately after the traced
       letter, stranding "Y" on a line of its own.

       So the wrapper takes the whole FIRST WORD — traced letter plus the rest of
       its characters — and is marked `nowrap`. A word is never broken internally
       anyway, so this removes the bad break without changing how the heading
       wraps everywhere else. */
    var rest = node.nodeValue.slice(idx + 1);
    var wordEnd = rest.search(/\s/);
    var wordTail = wordEnd === -1 ? rest : rest.slice(0, wordEnd);

    var after = node.splitText(idx);
    after.splitText(1 + wordTail.length);

    var span = document.createElement('span');
    span.className = 'sbd-shine';

    var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('class', 'sbd-shine__svg');
    svg.setAttribute('focusable', 'false');

    var text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    text.setAttribute('class', 'sbd-shine__trace');
    text.textContent = letter;
    svg.appendChild(text);
    span.appendChild(svg);
    if (wordTail) span.appendChild(document.createTextNode(wordTail));

    after.parentNode.replaceChild(span, after);

    h1.dataset.sbdShine = '1';
    layoutShine(span);
    return span;
  }

  function layoutShine(span) {
    var svg = span.querySelector('.sbd-shine__svg');
    var text = span.querySelector('.sbd-shine__trace');
    if (!svg || !text) return;

    var letter = text.textContent;
    // Inherit type from the heading, not from the span, so the glyph matches
    // the characters beside it exactly.
    var cs = getComputedStyle(span.parentElement || span);
    var size = parseFloat(cs.fontSize) || 16;
    var extra = (cs.letterSpacing && cs.letterSpacing !== 'normal')
      ? (parseFloat(cs.letterSpacing) || 0) : 0;

    var metrics = measure(letter, cs);
    if (!metrics || !metrics.width) return;

    /* Baseline alignment, without trusting any of my own arithmetic.

       The SVG is an inline-block with `vertical-align: baseline`, so the browser
       aligns its BOTTOM EDGE to the surrounding text's baseline. Placing the
       glyph's own baseline exactly on that bottom edge (y = height) makes the two
       baselines the same line by construction.

       The first attempt instead computed a baseline offset from canvas metrics
       and corrected with a negative margin. The advance width it produced was
       right to a twentieth of a pixel, but the vertical placement was off by ~6px
       — enough that the letter read as a different size from its neighbours.
       Descenders are drawn outside the box; `overflow: visible` keeps them. */
    var pad = size * 0.08;                       // room for the glow, not layout
    var w = Math.ceil(metrics.width + extra + pad * 2);
    var h = Math.ceil(metrics.ascent + pad);

    span.style.width = '';
    span.style.height = '';
    span.style.marginBottom = '';

    svg.setAttribute('width', w);
    svg.setAttribute('height', h);
    svg.setAttribute('viewBox', '0 0 ' + w + ' ' + h);
    // Negative margins keep the glow padding from displacing adjacent glyphs.
    svg.style.marginLeft = -pad + 'px';
    svg.style.marginRight = (-pad + extra) + 'px';

    text.setAttribute('x', pad);
    text.setAttribute('y', h);
    text.setAttribute('font-family', cs.fontFamily);
    text.setAttribute('font-size', cs.fontSize);
    text.setAttribute('font-weight', cs.fontWeight);
    text.setAttribute('font-style', cs.fontStyle);

    // Dash gap must exceed the longest contour so only one dot rides each
    // contour at a time. Six em is comfortably clear of any glyph.
    var len = Math.max(240, size * 6);
    text.style.strokeDasharray = SHINE_DASH + ' ' + len;
    text.style.setProperty('--sbd-shine-len', (len + SHINE_DASH) + 'px');
    text.style.strokeWidth = Math.max(1, size / 44) + 'px';
    span.dataset.sbdLaid = '1';
  }

  function measure(letter, cs) {
    try {
      if (!measureCtx) measureCtx = document.createElement('canvas').getContext('2d');
      measureCtx.font = fontShorthand(cs);
      var m = measureCtx.measureText(letter);
      var size = parseFloat(cs.fontSize) || 16;
      var asc = (typeof m.actualBoundingBoxAscent === 'number' && isFinite(m.actualBoundingBoxAscent))
        ? m.actualBoundingBoxAscent : size * 0.72;
      var desc = (typeof m.actualBoundingBoxDescent === 'number' && isFinite(m.actualBoundingBoxDescent))
        ? m.actualBoundingBoxDescent : size * 0.2;
      return { width: m.width, ascent: asc, descent: Math.max(desc, size * 0.05) };
    } catch (e) {
      return null;
    }
  }

  function relayoutAll() {
    document.querySelectorAll('.sbd-shine').forEach(layoutShine);
  }

  function initShine() {
    if (reduceMotion.matches) return;

    document.querySelectorAll(SHINE_SELECTOR).forEach(function (h1) {
      // A heading with no layout yet (inside a collapsed section, or a template
      // block that renders empty) cannot be measured. Decorating it anyway is
      // what produced a zero-width artefact in the page-title H1 on the first
      // pass, so those are watched and decorated once they actually appear.
      if (h1.getBoundingClientRect().width > 0) {
        buildShine(h1);
      } else if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (entries, obs) {
          if (entries[0].boundingClientRect.width > 0) {
            buildShine(h1);
            obs.disconnect();
          }
        });
        io.observe(h1);
      }
    });

    // Webfonts (Italiana) usually resolve after first paint, which changes both
    // the advance width and the outline the dot follows.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(relayoutAll).catch(function () {});
    }

    var t;
    window.addEventListener('resize', function () {
      clearTimeout(t);
      t = setTimeout(relayoutAll, 150);
    }, { passive: true });
  }

  /* ══════════════════════════════════════════════════════════════════════
     ITEMS 5 + 10 · Chat launcher
     ══════════════════════════════════════════════════════════════════════ */

  // Speech bubble, stroked to match the launcher's existing line weight.
  var CHAT_ICON =
    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' " +
    "stroke='%231b1512' stroke-width='1.9' stroke-linecap='round' stroke-linejoin='round'%3E" +
    "%3Cpath d='M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z'/%3E" +
    "%3C/svg%3E";

  var LAUNCHER_CSS = [
    '@media (max-width: 749px) {',
    /* ITEM 10 — a small round button with an unmistakable chat icon. The pill
       was ~160x60; the circle is 56x56, which is the smallest comfortable
       touch target and roughly a third of the footprint. */
    '  #launcher {',
    '    width: 56px !important;',
    '    height: 56px !important;',
    '    min-width: 0 !important;',
    '    padding: 0 !important;',
    '    border-radius: 50% !important;',
    '    display: grid !important;',
    '    place-items: center !important;',
    '    gap: 0 !important;',
    '    box-shadow: 0 6px 20px rgba(0,0,0,.42) !important;',
    '  }',
    /* The visible label is dropped, so the accessible name now comes solely
       from the button's existing aria-label ("Open chat with Vivi"). */
    '  #launcher > span:not(.dot):not(#unread) { display: none !important; }',
    '  #launcher .dot {',
    '    font-size: 0 !important;',
    '    line-height: 0 !important;',
    '    width: 26px !important;',
    '    height: 26px !important;',
    '    background-image: url("' + CHAT_ICON + '") !important;',
    '    background-size: contain !important;',
    '    background-repeat: no-repeat !important;',
    '    background-position: center !important;',
    '    animation: none !important;',
    '  }',
    '  #launcher #unread {',
    '    position: absolute !important;',
    '    top: -2px !important;',
    '    right: -2px !important;',
    '  }',
    /* ITEM 5 — lift clear of the sticky buy bar. The offset is written to the
       host as a custom property by liftLauncher() below. */
    '  #launcher {',
    '    bottom: calc(18px + var(--sbd-launcher-lift, 0px)) !important;',
    '    right: 16px !important;',
    '    transition: bottom .22s cubic-bezier(.22,1,.36,1) !important;',
    '  }',
    '}'
  ].join('\n');

  function styleLauncher(host) {
    var root = host.shadowRoot;
    if (!root || root.getElementById('sbd-launcher-polish')) return false;
    var style = document.createElement('style');
    style.id = 'sbd-launcher-polish';
    style.textContent = LAUNCHER_CSS;
    root.appendChild(style);
    return true;
  }

  /* ITEM 5 · Keep the launcher off the buy controls.

     The theme already had an IntersectionObserver that set the launcher to
     `visibility:hidden` while the CTA row was on screen. It did not help here:
     it only ran on templates that render `.buy-buttons-block .product-form-buttons`,
     so on the customizer pages the pill stayed put — and hiding the only route
     to support at the moment someone is deciding to buy is the wrong trade
     anyway. This lifts it above whatever is docked at the bottom instead. */
  function liftLauncher(host) {
    if (!window.matchMedia(MOBILE).matches) return;

    var docked = function () {
      return document.querySelector('.sticky-add-to-cart__bar, .sbd-sticky-bar, [class*="sticky-add-to-cart"]');
    };

    var apply = function () {
      var bar = docked();
      var lift = 0;
      if (bar) {
        var r = bar.getBoundingClientRect();
        var visible = r.bottom > 0 && r.top < window.innerHeight && r.height > 0;
        if (visible) lift = Math.max(0, window.innerHeight - r.top) + 12;
      }
      host.style.setProperty('--sbd-launcher-lift', lift + 'px');
    };

    apply();
    window.addEventListener('scroll', apply, { passive: true });
    window.addEventListener('resize', apply, { passive: true });
  }

  function initLauncher() {
    var tries = 0;
    var timer = setInterval(function () {
      var host = document.getElementById('vivi-widget-host');
      if (host && host.shadowRoot) {
        styleLauncher(host);
        liftLauncher(host);
        clearInterval(timer);
      } else if (++tries > 40) {
        // ~20s. The widget script is third-party; if it never mounts there is
        // nothing to restyle and nothing to clean up.
        clearInterval(timer);
      }
    }, 500);
  }

  /* ══════════════════════════════════════════════════════════════════════
     ITEM 4 · Customizer directly under the hero on /pages/design-your-companion
     ══════════════════════════════════════════════════════════════════════

     The page is two sections: `main` (the Shopify page body) and `studio` (the
     customizer). The page body is not just a hero — it is the hero followed by
     ~20KB of marketing: the four steps, the faces grid, craft, features, why-us
     and the assurances. So the customizer rendered below all of it, and someone
     arriving to design a doll had to scroll past the entire pitch to reach the
     thing they came for.

     Section order in the template JSON cannot help, because the hero and the rest
     of that content are a single section. Splitting the page body in admin would
     work but edits live content. Moving the node is the reversible option, and it
     is the pattern the theme already uses to relocate the customizer on the PDP.
  */
  function initStudioPlacement() {
    // Scoped to this one page on purpose. The homepage carries its own instance
    // of the customizer whose placement is deliberate, and a selector loose
    // enough to catch both would silently rearrange it too.
    if (!/^\/pages\/design-your-companion\/?$/.test(window.location.pathname)) return;

    var studio = document.querySelector('[id$="__studio"]');
    var hero = document.querySelector('header.hero');
    if (!studio || !hero || studio.dataset.sbdMoved) return;

    // Only act when the customizer really does sit after the hero's content;
    // if a future template already orders them correctly, leave it alone.
    if (hero.compareDocumentPosition(studio) & Node.DOCUMENT_POSITION_FOLLOWING) {
      hero.insertAdjacentElement('afterend', studio);
      studio.dataset.sbdMoved = '1';
    }
  }

  /* ══════════════════════════════════════════════════════════════════════
     ITEM 6 · The homepage's faces under "Four faces to begin with"
     ══════════════════════════════════════════════════════════════════════

     The page body's own faces grid showed the four heads as flat, single-tone
     background images. The homepage shows the same four — Luna/Suki/Penny/Kitty,
     the identical image files — as cards with a four-tone skin switcher.

     Rather than reimplement that card (markup, styles and behaviour) a second
     time, the `sbd-heads` section is added to this page's template with its
     intro block switched off, and moved into the grid's place here. One
     component, one stylesheet, one set of behaviour — reimplementing it is how
     this codebase ends up with a fix that exists on one surface and not the
     other.
  */
  function initFacesGrid() {
    if (!/^\/pages\/design-your-companion\/?$/.test(window.location.pathname)) return;

    var heads = document.querySelector('[id$="__faces"]');
    var grid = document.querySelector('.faces-grid');
    if (!heads || !grid || heads.dataset.sbdMoved) return;

    grid.insertAdjacentElement('afterend', heads);
    // Hidden rather than removed: the original markup stays in the page body, so
    // turning this off is a one-line revert and nothing is lost in admin.
    grid.setAttribute('hidden', '');
    grid.style.display = 'none';
    heads.dataset.sbdMoved = '1';
  }

  function boot() {
    initShine();
    initLauncher();
    initStudioPlacement();
    initFacesGrid();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
